import {
  CAlert,
  CButton,
  CCloseButton,
  CCol,
  COffcanvas,
  COffcanvasBody,
  COffcanvasHeader,
  COffcanvasTitle,
  CForm,
  CFormFeedback,
  CFormInput,
  CFormLabel,
  CLoadingButton,
} from '@coreui/react-pro'
import moment from 'moment'
import React, { useRef, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import RoundService from 'src/service/RoundService'
import ToastComponent from 'src/components/common/TaostComponent'

const AddForm = ({ visible, onClose, roundNumber, fetchUsers, seasonDates }) => {
  const [loader, setLoader] = useState(false)
  const [errorMessages, setErrorMessages] = useState([])
  const keepOpenRef = useRef(false)

  // Native date inputs take "YYYY-MM-DD" bounds; the season window limits what can be picked.
  const seasonMin = seasonDates?.start ? moment(seasonDates.start).format('YYYY-MM-DD') : undefined
  const seasonMax = seasonDates?.end ? moment(seasonDates.end).format('YYYY-MM-DD') : undefined

  const validationSchema = Yup.object().shape({
    round: Yup.number().required('Round is required'),
    start_date: Yup.date().required('Start Date is required'),
    end_date: Yup.date()
      .required('End Date is required')
      .min(Yup.ref('start_date'), 'End Date must be on or after Start Date'),
    start_time: Yup.string().required('Start Time is required'),
    end_time: Yup.string().required('End Time is required'),
  })

  const formik = useFormik({
    initialValues: {
      round: roundNumber,
      start_date: '',
      end_date: '',
      start_time: '',
      end_time: '',
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (data, actions) => {
      setLoader(true)
      RoundService.saveRound(data)
        .then((res) => {
          if (res.status === 200) {
            setErrorMessages([])
            fetchUsers()
            ToastComponent(res.message, 'success')
            resetFields(actions)
            if (!keepOpenRef.current) {
              onClose()
            }
          } else {
            const errors =
              res.errors && Array.isArray(res.errors)
                ? res.errors
                : [res.message || 'Something went wrong.']
            setErrorMessages(errors)
          }
          setLoader(false)
        })
        .catch(() => {
          ToastComponent('Something went wrong. Please try again.', 'error')
          setLoader(false)
        })
    },
  })

  const resetFields = (actions) => {
    actions.resetForm({
      values: { round: '', start_date: '', end_date: '', start_time: '', end_time: '' },
    })
  }

  const handleClose = () => {
    resetFields(formik)
    setErrorMessages([])
    onClose()
  }

  const submitForm = (keepOpen) => {
    keepOpenRef.current = keepOpen
    formik.handleSubmit()
  }

  // Native inputs hand back "YYYY-MM-DD" / "HH:mm", which is exactly what the API expects.
  const handleStartDate = (e) => {
    const value = e.target.value
    formik.setFieldValue('start_date', value)
    // Keep an end date that still falls after the new start; otherwise make them pick again.
    if (formik.values.end_date && value && formik.values.end_date < value) {
      formik.setFieldValue('end_date', '')
    }
  }

  return (
    <COffcanvas
      placement="end"
      visible={visible}
      onHide={handleClose}
      backdrop
      style={{ width: 480 }}
    >
      <COffcanvasHeader>
        <div>
          <COffcanvasTitle>Add Round</COffcanvasTitle>
          <div className="text-medium-emphasis small">
            Create round {roundNumber}, its lockout window and trade settings.
          </div>
        </div>
        <CCloseButton className="text-reset" onClick={handleClose} />
      </COffcanvasHeader>
      <COffcanvasBody className="d-flex flex-column">
        {errorMessages.length > 0 && (
          <div className="error-list">
            {errorMessages.map((error, index) => (
              <CAlert color="danger" key={index}>
                {error}
              </CAlert>
            ))}
          </div>
        )}
        <CForm className="row g-3 flex-grow-1" onSubmit={formik.handleSubmit}>
          <CCol xs={12}>
            <CFormLabel htmlFor="round">Round</CFormLabel>
            <CFormInput
              placeholder="Round"
              value={roundNumber ?? ''}
              aria-label="round"
              id="round"
              name="round"
              disabled
            />
            {formik.errors.round && formik.touched.round && (
              <CFormFeedback invalid className="d-block">
                {formik.errors.round}
              </CFormFeedback>
            )}
          </CCol>
          <CCol xs={6}>
            <CFormLabel htmlFor="start_date">Round Start Date *</CFormLabel>
            <CFormInput
              type="date"
              id="start_date"
              name="start_date"
              min={seasonMin}
              max={seasonMax}
              value={formik.values.start_date}
              invalid={!!(formik.errors.start_date && formik.touched.start_date)}
              onChange={handleStartDate}
              onBlur={formik.handleBlur}
            />
            {formik.errors.start_date && formik.touched.start_date && (
              <CFormFeedback invalid className="d-block">
                {formik.errors.start_date}
              </CFormFeedback>
            )}
          </CCol>
          <CCol xs={6}>
            <CFormLabel htmlFor="start_time">Round Start Time *</CFormLabel>
            <CFormInput
              type="time"
              id="start_time"
              name="start_time"
              value={formik.values.start_time}
              invalid={!!(formik.errors.start_time && formik.touched.start_time)}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.errors.start_time && formik.touched.start_time && (
              <CFormFeedback invalid className="d-block">
                {formik.errors.start_time}
              </CFormFeedback>
            )}
          </CCol>
          <CCol xs={6}>
            <CFormLabel htmlFor="end_date">Round End Date *</CFormLabel>
            <CFormInput
              type="date"
              id="end_date"
              name="end_date"
              min={formik.values.start_date || seasonMin}
              max={seasonMax}
              value={formik.values.end_date}
              invalid={!!(formik.errors.end_date && formik.touched.end_date)}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.errors.end_date && formik.touched.end_date && (
              <CFormFeedback invalid className="d-block">
                {formik.errors.end_date}
              </CFormFeedback>
            )}
          </CCol>
          <CCol xs={6}>
            <CFormLabel htmlFor="end_time">Round End Time *</CFormLabel>
            <CFormInput
              type="time"
              id="end_time"
              name="end_time"
              value={formik.values.end_time}
              invalid={!!(formik.errors.end_time && formik.touched.end_time)}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.errors.end_time && formik.touched.end_time && (
              <CFormFeedback invalid className="d-block">
                {formik.errors.end_time}
              </CFormFeedback>
            )}
          </CCol>
        </CForm>
      </COffcanvasBody>
      <div className="d-flex justify-content-between gap-2 p-3 border-top">
        <CButton color="secondary" variant="outline" onClick={handleClose}>
          Cancel
        </CButton>
        <div className="d-flex gap-2">
          <CLoadingButton
            color="secondary"
            variant="outline"
            loading={loader}
            onClick={() => submitForm(true)}
          >
            Save &amp; Add Another
          </CLoadingButton>
          <CLoadingButton color="primary" loading={loader} onClick={() => submitForm(false)}>
            Create Round
          </CLoadingButton>
        </div>
      </div>
    </COffcanvas>
  )
}

export default AddForm
